<template>
  <SoDialog
    v-model="value"
    card-title="Cancel Payment Plan"
    data-testid="cancel-payment-plan-modal"
  >
    <p>
      A payment plan is ongoing for this account - are you sure you want to
      cancel it?
    </p>
    <p class="my-5">
      Cancelling this payment plan will allow you to set up a new one if needed.
    </p>
    <p>
      Even if this payment plan is cancelled, any payments scheduled within the
      next 7 days are still likely to go ahead because of how banking systems
      work.
    </p>
    <div class="d-flex justify-end mt-5">
      <SoButton title="Cancel" large @click="value = false" />
      <SoButton
        title="Yes, Cancel Payment Plan"
        type="danger"
        class="ml-3"
        large
        :loading="isCancellingPaymentPlan"
        :disabled="isCancellingPaymentPlan"
        data-testid="cancel-payment-plan-button"
        @click="cancelPaymentPlan"
      />
    </div>
    <SoAlert v-if="error" type="error" :text="error" class="mt-6" />
  </SoDialog>
</template>

<script lang="ts">
  import SoAlert from "@/components/SoAlert.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { PAYMENT_PLAN } from "@/constants/paymentPlan"
  import { DebtPaymentPlanMutator } from "@/mutators/debtPaymentPlan/DebtPaymentPlanMutator"
  import { errorReporter } from "@/services/ErrorService"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { defineComponent } from "vue"

  export default defineComponent({
    components: {
      SoDialog,
      SoButton,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data: function () {
      return {
        current: useCurrentAccountStore(),
        isCancellingPaymentPlan: false,
        error: "",
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },
    methods: {
      async cancelPaymentPlan() {
        this.error = ""
        this.isCancellingPaymentPlan = true
        const juniferAccountId = this.current.account.data.juniferId
        const paymentPlanId = this.current.activeDebtPaymentPlan.data?.id
        const planType =
          this.current.activeDebtPaymentPlan.data?.type ?? PAYMENT_PLAN
        try {
          if (!paymentPlanId) throw new Error("Missing required data")
          await DebtPaymentPlanMutator.cancelForAccount(
            juniferAccountId,
            planType,
          ).mutateAsync({ paymentPlanId })
          await this.current.debtPaymentPlans.refetch()
          this.value = false // close modal
        } catch (error) {
          this.error =
            "Something went wrong. Please refresh the page and try again."
          errorReporter.report(error)
          console.error("Error cancelling payment plan", error)
        }
        this.isCancellingPaymentPlan = false
      },
    },
  })
</script>
